/**
 * Maps axios errors from /api/trainer to messages for the user. The backend sends handled errors as
 * { "error": "..." } (see TrainerExceptionHandler and GarminConnectionController). Messages from the
 * server are shown when they are safe to show; they never contain keys or tokens.
 */

interface AxiosLikeError {
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
}

/** Message from the backend's { error } body, or undefined. Blob bodies (downloads) are ignored. */
export function serverMessage(err: unknown): string | undefined {
  const data = (err as AxiosLikeError)?.response?.data;
  if (data && typeof data === 'object' && !(data instanceof Blob)) {
    const error = (data as { error?: unknown }).error;
    if (typeof error === 'string' && error.trim()) return error;
  }
  return undefined;
}

/** HTTP status of a failed request, if the server answered. */
export function statusOf(err: unknown): number | undefined {
  return (err as AxiosLikeError)?.response?.status;
}

/** Seconds from the Retry-After header of a 429, or undefined. */
export function retryAfterSeconds(err: unknown): number | undefined {
  const headers = (err as AxiosLikeError)?.response?.headers;
  if (!headers) return undefined;
  const raw = headers['retry-after'] ?? headers['Retry-After'];
  const value = Number(raw);
  return raw !== undefined && Number.isFinite(value) && value > 0 ? Math.ceil(value) : undefined;
}

/** True when the failure is an expired or missing login (401). */
export function isUnauthorized(err: unknown): boolean {
  return statusOf(err) === 401;
}

/** True when the login is valid but has no trainer access (403), e.g. a remember-me session. */
export function isForbidden(err: unknown): boolean {
  return statusOf(err) === 403;
}

/** Human-readable message for an axios or generic error. */
export function errorMessage(err: unknown): string {
  const status = statusOf(err);
  const server = serverMessage(err);
  switch (status) {
    case 400:
      return server ?? 'The request was not accepted. Check the values and try again.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'This login has no trainer access. Log in again to continue.';
    case 404:
      return server ?? 'Not found.';
    case 409:
      return server ?? 'That action is not available right now.';
    case 413:
      return 'This account has too much data to export in one file.';
    case 429:
      return server ?? 'Too many requests. Try again in a few minutes.';
    case 502:
      return server ?? 'Garmin is not available right now. Try again later.';
    case 503:
      return 'Garmin connection is not set up on this server yet (the encryption key is not configured). Ask the site owner to enable it.';
    default:
      break;
  }
  if (status && status >= 500) return 'The server had a problem. Try again later.';
  if (!status) return 'Could not reach the server. Check your connection and try again.';
  return (err as AxiosLikeError)?.message ?? 'Something went wrong.';
}
